import { deleteObject, getBytes, listAll, ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase/config';
import { userStorageQuotaService } from './userStorageQuotaService';
import {
  buildGeneralAssignmentSubmissionPrefix,
  buildProjectAssignmentSubmissionPrefix,
  buildProjectSubTaskSubmissionPrefix
} from '../utils/assignmentSubmissionImagePaths';

const deleteFolderRecursive = async (folderPath: string): Promise<void> => {
  const folderRef = storageRef(storage, folderPath.replace(/\/$/, ''));
  try {
    const list = await listAll(folderRef);
    await Promise.all(list.items.map((itemRef) => deleteObject(itemRef)));
    await Promise.all(list.prefixes.map((sub) => deleteFolderRecursive(sub.fullPath)));
  } catch (error: any) {
    if (error?.code === 'storage/object-not-found') return;
    throw error;
  }
};

const deleteAllUnderPrefix = async (prefix: string): Promise<void> => {
  await deleteFolderRecursive(prefix);
  userStorageQuotaService.invalidateUsageCache();
};

export const assignmentSubmissionImageService = {
  async uploadTeacherBlob(path: string, blob: Blob, contentType: string, userId?: string): Promise<void> {
    if (userId) {
      await userStorageQuotaService.assertCanUpload(userId, blob.size, path);
    }
    const fileRef = storageRef(storage, path);
    await uploadBytes(fileRef, blob, {
      contentType,
      cacheControl: 'public, max-age=31536000, immutable'
    });
    userStorageQuotaService.invalidateUsageCache();
  },

  async deleteByPath(path: string): Promise<void> {
    try {
      await deleteObject(storageRef(storage, path));
      userStorageQuotaService.invalidateUsageCache();
    } catch (error: any) {
      if (error?.code === 'storage/object-not-found') return;
      throw error;
    }
  },

  async getBytesByPath(path: string): Promise<ArrayBuffer> {
    return getBytes(storageRef(storage, path));
  },

  async deleteGeneralAssignmentImages(
    teacherUserId: string,
    classId: string,
    memberGroup: string | undefined,
    assignmentId: string
  ): Promise<void> {
    const prefix = buildGeneralAssignmentSubmissionPrefix(
      teacherUserId,
      classId,
      memberGroup,
      assignmentId
    );
    await deleteAllUnderPrefix(prefix);
  },

  async deleteProjectAssignmentImages(
    teacherUserId: string,
    classId: string,
    memberGroup: string | undefined,
    projectId: string
  ): Promise<void> {
    const prefix = buildProjectAssignmentSubmissionPrefix(
      teacherUserId,
      classId,
      memberGroup,
      projectId
    );
    await deleteAllUnderPrefix(prefix);
  },

  async deleteProjectSubTaskImages(
    teacherUserId: string,
    classId: string,
    memberGroup: string | undefined,
    projectId: string,
    subTaskId: string
  ): Promise<void> {
    const prefix = buildProjectSubTaskSubmissionPrefix(
      teacherUserId,
      classId,
      memberGroup,
      projectId,
      subTaskId
    );
    await deleteAllUnderPrefix(prefix);
  }
};
